/* eslint-disable prettier/prettier */
import { Grid, TextField, Box, Typography, Button } from "@material-ui/core";
import InfoTooltip from "../InfoTooltip";
import ImageUpload from "../ImageUpload";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../api/AppContext";
import { getFieldProps } from "../utils";

// hacky way to allow file upload for Allitude
const ALLITUDE_COMPANY_ID = "21330221-0072-45a1-88e1-d6ad6de3bf40";

const getCustomPathInfo = (user) => {
  if (user.companies?.includes(ALLITUDE_COMPANY_ID)) {
    return {
      journeyId:
        process.env.REACT_APP_DASHBOARD_STAGE === "qa"
          ? "afb4b4d1-26de-40fe-b336-fc5167b76204"
          : "7b9743a2-fae7-4a03-95a1-0d62bfe1839a"
    };
  } else {
    return { cid: "67df6292-c0a7-4831-a94e-61b6ea73c990" };
  }
};

const Tools = ({ formik }) => {
  const location = useLocation();
  const basePath = useMemo(
    () => location.pathname.split("/")[1],
    [location.pathname]
  );
  const isKnowledgeHub = basePath === "knowledge-hub";
  const { user } = useAppContext();

  const customPathInfo = getCustomPathInfo(user);

  const handleInputChange = (name, value) => {
    formik.setFieldValue(name, value);
  };

  return (
    <Box
      alignContent="center"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid container spacing={2}>
        <Grid container item justifyContent="center" xs={12}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              label="Name"
              margin="none"
              value={formik.values.toolname}
              variant="outlined"
              onChange={(event) =>
                handleInputChange("toolname", event.target.value)
              }
            />
          </Grid>
        </Grid>
        <Grid container item justifyContent="center" xs={12}>
          <Grid item>
            <Typography
              color="textSecondary"
              style={{ padding: "0px 4px 0px 0px" }}
              variant="caption"
            >
              Icon
            </Typography>
            <InfoTooltip
              style={{ padding: "0px 0px 0px 4px" }}
              text="Upload .jpg or .png files. The uploaded picture will be displayed with 960px width and 320px height. In case of size mismatch, the picture will be scaled horizontally to 960px and cropped vertically (a central stripe of 320px height will be taken)"
              title="Icon Dimensions"
            />
            <ImageUpload
              styles={{ width: "100%" }}
              {...getFieldProps(formik, {
                name: "toolicon"
              })}
              customPathInfo={customPathInfo}
              onChange={({ url }) => {
                formik.setFieldValue("toolicon", url);
                formik.setFieldTouched("toolicon", true);
              }}
            />
          </Grid>
        </Grid>
        <Grid container item justifyContent="center" xs={12}>
          <Grid item>
            <Typography
              color="textSecondary"
              style={{ padding: "0px 4px 0px 0px" }}
              variant="caption"
            >
              Icon 2 {isKnowledgeHub ? "(Required)" : "(Optional)"}
            </Typography>
            <InfoTooltip
              style={{ padding: "0px 0px 0px 4px" }}
              text="Upload .jpg or .png files. The uploaded picture will be displayed with 960px width and 320px height. In case of size mismatch, the picture will be scaled horizontally to 960px and cropped vertically (a central stripe of 320px height will be taken)"
              title="Icon Dimensions"
            />
            <ImageUpload
              styles={{ width: "100%" }}
              {...getFieldProps(formik, {
                name: "toolicon2"
              })}
              customPathInfo={customPathInfo}
              onChange={({ url }) => {
                formik.setFieldValue("toolicon2", url);
                formik.setFieldTouched("toolicon2", true);
              }}
            />
          </Grid>
        </Grid>
        <Grid container item justifyContent="space-around" xs={12}>
          <Grid>
            <Typography
              color="textSecondary"
              style={{ padding: "0px 4px 0px 0px" }}
              variant="caption"
            >
              Color Top
            </Typography>
            <input
              type="color"
              value={formik.values.colortop}
              onChange={(event) =>
                handleInputChange("colortop", event.target.value)
              }
            />
          </Grid>
          <Grid>
            <input
              type="color"
              value={formik.values.colorbottom}
              onChange={(event) =>
                handleInputChange("colorbottom", event.target.value)
              }
            />
            <Typography
              color="textSecondary"
              style={{ padding: "0px 0px 0px 4px" }}
              variant="caption"
            >
              Color Bottom
            </Typography>
          </Grid>
        </Grid>
        {basePath === "copilot" && (
          <Grid container item justifyContent="center" xs={12}>
            <Grid item style={{ textAlign: "center" }} xs={4}>
              <Typography color="textSecondary" variant="caption">
                Gradient Color (Needed for CopilotGoV2)
              </Typography>
              <TextField
                fullWidth
                InputLabelProps={{ shrink: true }}
                label="Gradient"
                margin="dense"
                placeholder="linear-gradient(232deg, #2DCCEA 0%, #C35FE4 50%, #E75DD7 100%)"
                value={formik.values.gradientcolor || ""}
                variant="outlined"
                onChange={(event) =>
                  handleInputChange("gradientcolor", event.target.value)
                }
              />
            </Grid>
          </Grid>
        )}
        <Grid container item justifyContent="center" xs={12}>
          <Button
            color="primary"
            type="submit"
            variant="contained"
            onClick={formik.handleSubmit}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

const ToolContainer = () => {
  const { updateTool } = useAppContext();
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [configurationEntity, setConfigurationEntity] = useState(null);
  const basePath = useMemo(
    () => location.pathname.split("/")[1],
    [location.pathname]
  );
  const isKnowledgeHub = basePath === "knowledge-hub";

  useEffect(() => {
    const { rowData } = location.state || {};
    if (rowData) {
      setConfigurationEntity({ ...rowData, id });
    }
  }, [id, location.state]);
  const validationSchema = Yup.object({
    colorbottom: Yup.string().required("Color Bottom is required"),
    colortop: Yup.string().required("Color Top is required"),
    gradientcolor: Yup.string().nullable(),
    toolicon: Yup.string().required("Icon is required"),
    toolicon2: isKnowledgeHub
      ? Yup.string().required("Icon 2 is required for Knowledge Hub")
      : Yup.string().nullable(),
    toolname: Yup.string().required("Name is required")
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: configurationEntity || {
      colorbottom: "",
      gradientcolor: "",
      colortop: "",
      isNew: true,
      toolicon: "",
      toolicon2: "",
      toolname: ""
    },

    onSubmit: async (values) => {
      const cleanedValues = { ...values };
      if (values.isNew) {
        cleanedValues.isNew = values.isNew;
      } else {
        cleanedValues.id = values.id;
      }
      await updateTool(cleanedValues, basePath);
      history.push(`/${basePath}/tools`);
    },
    validationSchema: validationSchema,
    validator: () => ({})
  });

  if (!formik.values) {
    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        width="100%"
      >
        <Typography variant="subtitle1">Tool is not found 😢</Typography>
      </Box>
    );
  }

  return <Tools formik={formik} />;
};

export default ToolContainer;
